import { Badge, Card } from "@vb/ui";
import { Seo } from "../../lib/seo.js";
import { Section, SectionHeading } from "../../components/Section.js";

const paragraph = { margin: 0, fontSize: 14, color: "var(--fg-2)", lineHeight: 1.6 } as const;
const subhead = { margin: "0 0 6px", fontSize: 16, color: "var(--fg-1)" } as const;
const link = { color: "var(--cyan)" } as const;
const listStyle = {
  margin: 0,
  paddingLeft: 20,
  fontSize: 14,
  color: "var(--fg-2)",
  lineHeight: 1.6,
  display: "grid",
  gap: 6,
} as const;

export function Terms() {
  return (
    <>
      <Seo
        title="Terms · Vault Breaker Studios"
        description="Binding Terms of Service for the Vault Breaker Studios website and the public play-money demo. Operator-side commercial terms are in the separate Master Services Agreement."
        path="/legal/terms"
      />

      <Section>
        <Badge kind="fair" style={{ marginBottom: 18 }}>
          LEGAL
        </Badge>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(44px, 6vw, 72px)",
            lineHeight: 1,
            margin: 0,
            color: "var(--fg-1)",
            textTransform: "uppercase",
          }}
        >
          Terms
        </h1>
        <p style={{ fontSize: 17, color: "var(--fg-2)", marginTop: 14, maxWidth: 760, lineHeight: 1.55 }}>
          This is a binding agreement between you and Vault Breaker Studios. It governs
          your use of this marketing site and the <code>/play</code> play-money demo. The
          operator-side commercial relationship is governed by a separate Master Services
          Agreement (MSA); see{" "}
          <a href="/docs/operator-msa-template.pdf" style={link}>
            /docs/operator-msa-template.pdf
          </a>{" "}
          (a signed copy is sent after we exchange terms).
        </p>
      </Section>

      <Section>
        <SectionHeading eyebrow="1" title="Definitions" />
        <Card style={{ display: "grid", gap: 12 }}>
          <ul style={listStyle}>
            <li>
              <strong>"Studio", "we", "us":</strong> Vault Breaker Studios Ltd
              (incorporation in progress) and its successors.
            </li>
            <li>
              <strong>"Site":</strong> the marketing pages hosted at vaultbreaker.dev,
              including the public demo at <code>/play</code> and the documentation pages.
            </li>
            <li>
              <strong>"Demo":</strong> the public, play-money games available at{" "}
              <code>/play</code> and any sub-routes thereof.
            </li>
            <li>
              <strong>"Operator":</strong> a regulated business that has signed, or is
              evaluating, a Master Services Agreement with the Studio to integrate the
              Studio's games into its own real-money gaming platform.
            </li>
            <li>
              <strong>"MSA":</strong> the Master Services Agreement between the Studio and
              an Operator. The MSA is the binding commercial document; this page is not.
            </li>
            <li>
              <strong>"You":</strong> the natural person accessing the Site, or the legal
              entity on whose behalf the access takes place.
            </li>
          </ul>
        </Card>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="2"
          title="Use of the demo (/play*)"
          lead="The demo exists to let prospective operators feel the mechanics. It is not a real-money product."
        />
        <Card style={{ display: "grid", gap: 12 }}>
          <ul style={listStyle}>
            <li>
              <strong>Play-money only.</strong> The Demo runs against an in-memory
              play-money wallet under the built-in <code>demo</code> operator. There are
              no winnings, no payouts, no withdrawal mechanism, and no transfer of value.
            </li>
            <li>
              <strong>18+ requirement.</strong> You must be at least eighteen (18) years
              old to access the Demo, even though no real-money risk exists. Where local
              law sets a higher minimum, the local minimum applies.
            </li>
            <li>
              <strong>Geographic restrictions.</strong> The Demo is not made available to
              users connecting from the United States, the United Kingdom, France, the
              Netherlands, Australia, Singapore, or Israel. We may add or remove
              jurisdictions as our compliance posture evolves.
            </li>
            <li>
              <strong>One account per user.</strong> The Demo issues an ephemeral session.
              You may not script, automate, multiplex, or otherwise generate sessions in
              bulk to abuse rate limits, distort metrics, or stress-test the public API.
            </li>
            <li>
              <strong>No reliance.</strong> The Demo is illustrative. It is not an
              endorsement of any operator and is not gambling within the meaning of any
              regulatory regime we are aware of. If your local regulator disagrees, do not
              use the Demo.
            </li>
          </ul>
        </Card>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="3"
          title="Operator engagement"
          lead="Everything on this Site that describes commercial terms is informational."
        />
        <Card style={{ display: "grid", gap: 12 }}>
          <p style={paragraph}>
            Pricing pages, integration overviews, RTP tables, launch timelines, and
            similar materials on this Site are provided for evaluation only. They do not
            constitute an offer, a binding quote, or a contract. The binding terms
            between the Studio and an Operator — including pricing, RTP commitments,
            uptime, jurisdiction warranties, payment terms, and data processing — are set
            out exclusively in the executed MSA and its order forms.
          </p>
          <p style={paragraph}>
            Submitting the contact form does not create a contractual relationship and
            does not obligate either party to enter into one.
          </p>
        </Card>
      </Section>

      <Section>
        <SectionHeading eyebrow="4" title="Intellectual property" />
        <Card style={{ display: "grid", gap: 12 }}>
          <p style={paragraph}>
            The Studio retains all right, title, and interest in and to the Site, the
            game mechanics, the underlying mathematics, the visual design, the brand
            marks, and any documentation, except where a specific component is published
            under an open-source licence.
          </p>
          <p style={paragraph}>
            The reference engine source code is currently licensed under the MIT licence.
            We may relicense the engine in future under a "Vault Breaker Public Licence"
            — a source-available licence with field-of-use restrictions — applied
            prospectively. Existing forks under MIT remain under MIT. (TBD; the final
            licence text will be published before any change takes effect.)
          </p>
          <p style={paragraph}>
            Operator-facing brand assets — the Studio name, logo, screenshots, and game
            keyart — may be used by an Operator only with the Studio's prior written
            consent or under a licence granted in the MSA. Press and review use is
            permitted under fair-use principles; do not imply endorsement we have not
            given.
          </p>
        </Card>
      </Section>

      <Section>
        <SectionHeading eyebrow="5" title="Acceptable use" />
        <Card style={{ display: "grid", gap: 12 }}>
          <p style={paragraph}>You agree not to:</p>
          <ul style={listStyle}>
            <li>
              Scrape, crawl, or harvest content from the Site at a rate or volume
              inconsistent with personal evaluation.
            </li>
            <li>
              Reverse engineer, decompile, or disassemble any part of the closed-source
              services for the purpose of building a competing product. (Auditing
              fairness — replaying seeds, verifying proofs, inspecting open-source engine
              code — is explicitly permitted and encouraged.)
            </li>
            <li>
              Run automated bots, headless browsers, or load-generation tools against the
              Demo, the public REST surface, or the contact form.
            </li>
            <li>
              Probe the Site for vulnerabilities outside of a coordinated disclosure
              process. If you find something, email{" "}
              <a href="mailto:hello@vaultbreaker.dev" style={link}>
                hello@vaultbreaker.dev
              </a>
              .
            </li>
            <li>
              Use the Site or the Demo to facilitate any activity that is illegal in your
              jurisdiction or in the United Kingdom.
            </li>
          </ul>
          <p style={paragraph}>
            We may throttle, suspend, or terminate your access at any time if we
            reasonably believe you are breaching these rules.
          </p>
        </Card>
      </Section>

      <Section>
        <SectionHeading eyebrow="6" title="Warranty disclaimer" />
        <Card>
          <p style={paragraph}>
            THE SITE AND THE DEMO ARE PROVIDED "AS IS" AND "AS AVAILABLE", WITHOUT
            WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. TO THE MAXIMUM EXTENT PERMITTED
            BY LAW, THE STUDIO DISCLAIMS ALL IMPLIED WARRANTIES INCLUDING MERCHANTABILITY,
            FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, AND ANY WARRANTY ARISING
            FROM COURSE OF DEALING OR USAGE OF TRADE. THE STUDIO DOES NOT WARRANT THAT
            THE SITE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.
          </p>
        </Card>
      </Section>

      <Section>
        <SectionHeading eyebrow="7" title="Limitation of liability" />
        <Card style={{ display: "grid", gap: 12 }}>
          <p style={paragraph}>
            To the maximum extent permitted by law, the Studio's aggregate liability
            arising out of or related to your use of the Site or the Demo, whether in
            contract, tort (including negligence), warranty, or otherwise, shall not
            exceed the total fees you paid the Studio in the twelve (12) months
            immediately preceding the event giving rise to the claim. For users of the
            Site and the Demo who are not paying Operators, that cap is{" "}
            <strong>zero</strong>.
          </p>
          <p style={paragraph}>
            Neither party shall be liable for indirect, incidental, special,
            consequential, exemplary, or punitive damages, or for lost profits, lost
            revenue, lost data, or business interruption, even if advised of the
            possibility. Nothing in these Terms limits liability for fraud, fraudulent
            misrepresentation, death or personal injury caused by negligence, or any
            liability that cannot be excluded as a matter of law.
          </p>
        </Card>
      </Section>

      <Section>
        <SectionHeading eyebrow="8" title="Indemnification" />
        <Card style={{ display: "grid", gap: 12 }}>
          <p style={paragraph}>
            <strong>Operator indemnity to the Studio.</strong> Each Operator, on the
            terms of its MSA, indemnifies, defends, and holds the Studio harmless from
            any third-party claim brought by an end player or by a regulator that arises
            out of the Operator's licensing posture, marketing, age verification,
            anti-money-laundering, payment processing, or end-user terms.
          </p>
          <p style={paragraph}>
            <strong>Studio indemnity to the Operator.</strong> The Studio indemnifies,
            defends, and holds the Operator harmless from any third-party claim alleging
            that the Studio's licensed software, in its unmodified form and as supplied
            under the MSA, infringes a third party's intellectual property rights.
          </p>
          <p style={paragraph}>
            Each indemnity is subject to: prompt written notice of the claim; sole
            control of the defence by the indemnifying party; and reasonable cooperation
            from the indemnified party. Neither party may settle a claim in a manner
            that imposes liability on the other without that other's prior written
            consent.
          </p>
        </Card>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="9"
          title="Governing law and venue"
        />
        <Card style={{ display: "grid", gap: 12 }}>
          <p style={paragraph}>
            These Terms are governed by the laws of England and Wales, without regard to
            conflict-of-laws principles. The courts of London, England shall have
            exclusive jurisdiction over any dispute arising out of or relating to these
            Terms or your use of the Site, save that either party may seek injunctive
            relief in any court of competent jurisdiction to protect its intellectual
            property.
          </p>
          <p style={paragraph}>{"// TODO: confirm jurisdiction with counsel"}</p>
        </Card>
      </Section>

      <Section>
        <SectionHeading eyebrow="10" title="Termination" />
        <Card>
          <p style={paragraph}>
            Either party may terminate these Terms for material breach by the other if
            the breach is not cured within thirty (30) days of written notice. The
            Studio may also terminate or suspend your access immediately, without
            notice, where continued access poses a security, legal, or regulatory risk.
            Sections 4 (Intellectual property), 6 (Warranty disclaimer), 7 (Limitation
            of liability), 8 (Indemnification), 9 (Governing law and venue), and any
            other provision that by its nature should survive, will survive termination.
          </p>
        </Card>
      </Section>

      <Section>
        <SectionHeading eyebrow="11" title="Changes to these Terms" />
        <Card>
          <p style={paragraph}>
            The Studio may update these Terms from time to time. For material changes —
            those that meaningfully reduce a user's rights or expand the user's
            obligations — we will give thirty (30) days' notice by updating the "Last
            updated" date and, where the change affects an Operator, by emailing the
            Operator's notice contact on file. Continued use of the Site after the
            effective date constitutes acceptance of the updated Terms.
          </p>
        </Card>
      </Section>

      <Section>
        <SectionHeading eyebrow="12" title="Contact" />
        <Card>
          <p style={paragraph}>
            Notices and questions:{" "}
            <a href="mailto:hello@vaultbreaker.dev" style={link}>
              hello@vaultbreaker.dev
            </a>
            . For the operator-facing MSA, the dedicated commercial channel will be
            opened during contracting; until then, the same address routes correctly.
          </p>
        </Card>
      </Section>

      <Section tight>
        <p style={{ fontSize: 13, color: "var(--fg-3)", margin: 0 }}>
          13. Last updated: 2026-05-05.
        </p>
      </Section>
    </>
  );
}
